import { DIETARY_LABEL, DIETARY_OPTIONS } from '../../dietary'
import type { DietaryRequirement } from '../../types'

interface DietaryPickerProps {
  selected: readonly DietaryRequirement[]
  disabled: boolean
  namePrefix: string
  onToggle: (requirement: DietaryRequirement) => void
}

/**
 * The dietary requirements a guest declares.
 *
 * Checkboxes rather than a single choice, because somebody can be both vegan and
 * gluten free. "None" is not an option to tick - it is simply none ticked, which
 * is one fewer thing to get wrong than a mutually exclusive "none" that has to
 * be kept in step with the others.
 */
export function DietaryPicker({ selected, disabled, namePrefix, onToggle }: DietaryPickerProps) {
  return (
    <fieldset disabled={disabled}>
      <legend className="text-ink-soft text-xs tracking-widest uppercase">
        Dietary requirements
      </legend>
      <p className="text-ink-soft mt-1 text-xs">Leave all unticked if there are none.</p>

      <div className="mt-2 flex flex-wrap gap-2">
        {DIETARY_OPTIONS.map((requirement) => {
          const id = `${namePrefix}-dietary-${requirement}`
          return (
            <div key={requirement}>
              <input
                type="checkbox"
                id={id}
                checked={selected.includes(requirement)}
                onChange={() => onToggle(requirement)}
                className="peer sr-only"
              />
              <label
                htmlFor={id}
                className="border-ink/15 hover:border-claret/50 peer-checked:border-claret peer-checked:bg-claret peer-focus-visible:outline-claret block cursor-pointer rounded-md border px-3 py-1.5 text-sm transition-colors peer-checked:text-white peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-60"
              >
                {DIETARY_LABEL[requirement]}
              </label>
            </div>
          )
        })}
      </div>
    </fieldset>
  )
}
